// src/merch/checkout.js
//
// Order pipeline (client side):
//   1. build print files locally (cut-sheet PDF, preview PNGs, design JSON)
//   2. merch-checkout "create" → order row + signed upload URLs
//   3. upload files straight to Storage
//   4. merch-checkout "pay" → Stripe Checkout URL (authorize only)

import { STORE } from "./stores";
import { previewPng } from "./preview";
import { contourFromCanvas } from "./contour";
import { snapshot } from "valtio";
import { buildCutSheet } from "../engine";
import { merch, layersForSide } from "./store";
import { commitPaintAsLayer } from "./paint";
import { getSides, cornerRadiusMm, printAreaFor, printSizeFor, BLEED_MM } from "./products";
import { stickerPath2D } from "./shapes";
import { buildPrintCanvas } from "./usePrintComposite";
import { supabase, callFn } from "./supabase";
import { saveDesign } from "./designs";

/** Zones ("sides") that actually have a design. */
export function sidesWithDesign(product, size) {
  return getSides(product, size, merch.hand).filter((s) => layersForSide(s.id).length);
}

/** Cut spec for a sticker (engine printPdf format). */
export function cutFor(sticker) {
  if (sticker.contour) {
    if (sticker.cutRings?.length) return { type: "rings", rings: sticker.cutRings };
    return { type: "roundRect", radiusMm: cornerRadiusMm(sticker) };
  }
  if (sticker.outline?.length) return { type: "poly", outline: sticker.outline };
  if (sticker.round) return { type: "ellipse" };
  return { type: "roundRect", radiusMm: cornerRadiusMm(sticker) };
}

/** One PDF: every designed zone stacked, each with its true cut shape.
 *  (`printSides` = zone objects from getSides.) */
export async function buildSheetPdf({ product, size, printSides }) {
  const canvases = [];
  for (const z of printSides) {
    canvases.push(await buildPrintCanvas(printAreaFor(z.sticker), z.sticker, BLEED_MM, printSizeFor(z.sticker), z.id));
  }
  // contour stickers: the cut line comes from the finished print artwork
  // (full resolution, bleed included) so it always matches what prints
  printSides = printSides.map((z, i) => {
    if (!z.sticker.contour) return z;
    const st = z.sticker, c = canvases[i];
    const mm = { w: st.widthMm + BLEED_MM * 2, h: st.heightMm + BLEED_MM * 2 };
    const { rings } = contourFromCanvas(c, { x: 0, y: 0, w: c.width, h: c.height }, mm, { pxPerMm: 6, ...contourOpts() });
    const lim = (v, max) => Math.min(max + BLEED_MM, Math.max(-BLEED_MM, v - BLEED_MM));
    return { ...z, sticker: { ...st, cutRings: rings.map((r) => r.map(([x, y]) => [lim(x, st.widthMm), lim(y, st.heightMm)])) } };
  });
  const blob = await buildCutSheet({
    canvases,
    pieces: printSides.map((z) => ({ widthMm: z.sticker.widthMm, heightMm: z.sticker.heightMm, cut: cutFor(z.sticker) })),
    bleedMm: BLEED_MM,
    title: `MerchMod — ${product.name} ${size.label} (${printSides.map((s) => s.label).join(" + ")})`,
  });
  return { blob, canvases, printSides };
}

/** The customer's contour settings (offset + one piece / separate). */
export function contourOpts() {
  return { offsetMm: merch.contour?.offsetMm ?? 3.175, single: merch.contour?.single ?? true };
}

/** Everything the reviewer (and auto-flags) need to know about the design. */
function designFacts(product, size) {
  const text = [];
  let hasUploads = false;
  for (const s of getSides(product, size, merch.hand)) {
    for (const L of layersForSide(s.id)) {
      if (L.kind === "text" && L.text?.text) text.push(L.text.text);
      if (L.kind === "upload" || L.kind === "scan" || L.kind === "face") hasUploads = true;
    }
  }
  return { designText: text, hasUploads };
}

/**
 * Place the order. `onStep(label)` reports progress for the UI.
 * Resolves after redirecting the browser to Stripe.
 */
export async function placeOrder({ product, size, colour, quantity, rightsConfirmed, marketing = false, onStep = () => {} }) {
  if (merch.paint.strokes) commitPaintAsLayer();
  let printSides = sidesWithDesign(product, size);
  if (!printSides.length) throw new Error("Add something to your design first.");

  onStep("Preparing print file…");
  const built = await buildSheetPdf({ product, size, printSides });
  const { blob: sheet, canvases } = built;
  printSides = built.printSides; // contour stickers now carry their cut line
  const previews = {};
  for (let i = 0; i < printSides.length; i++) {
    const st = printSides[i].sticker;
    previews[`${printSides[i].id}.png`] = await previewPng(canvases[i], colour.hex, st, st.widthMm / st.heightMm > 6 ? 1600 : 900);
  }
  const s = snapshot(merch);
  const design = {
    version: 1,
    productId: s.productId, sizeId: s.sizeId, colourId: s.colourId,
    hand: s.hand,
    contour: s.contour, // die-cut settings (border + one piece / separate)
    sides: Object.fromEntries(getSides(product, size, s.hand).map((sd) => [sd.id, layersForSide(sd.id)])),
  };
  const designBlob = new Blob([JSON.stringify(design)], { type: "application/json" });
  const facts = designFacts(product, size);
  // frozen snapshot → the order's 3D view link (never changes, even if the
  // family keeps editing their own copy)
  onStep("Saving your design…");
  const snap3d = await saveDesign({ frozen: true });

  onStep("Saving your design…");
  const created = await callFn("merch-checkout", {
    action: "create",
    appBaseUrl: window.location.href,
    order: {
      productId: s.productId,
      sizeId: s.sizeId,
      colourId: s.colourId,
      sides: printSides.map((x) => x.id),
      quantity,
      rightsConfirmed,
      hand: s.hand,
      designId: snap3d.id,
      store: STORE.id, // which white-label store took the order
      marketing: marketing === true,
      ...facts,
    },
  });

  const blobs = { "sheet.pdf": sheet, "design.json": designBlob, ...previews };
  const bucket = supabase.storage.from("merch-orders");
  for (const [name, up] of Object.entries(created.uploads || {})) {
    const b = blobs[name];
    if (!b) continue;
    const { error } = await bucket.uploadToSignedUrl(up.path, up.token, b, { contentType: b.type, upsert: true });
    if (error) throw new Error(`Upload failed (${name}) — please try again.`);
  }

  onStep("Opening secure checkout…");
  const { url } = await callFn("merch-checkout", {
    action: "pay",
    orderId: created.orderId,
    accessToken: created.accessToken,
    appBaseUrl: window.location.href,
  });
  window.location.href = url;
}
